// packages import
import { Autocomplete, TextField } from '@mui/material'
import { FC, useEffect, useState, useCallback, memo } from 'react'

// others
import { ExternalUserCaseRole, Maybe, useGetCaseClientsLazyQuery } from '../../../../generated'
import { GRAPHQL_QUERY_POLICY } from '../../../constants'
import { enumToLabel } from '../../../utils'

export interface ClientOption {
  id: string
  label: string
  caseRole: ExternalUserCaseRole | null
  clientNumber: string
}

interface DepositClientDropdownProps {
  caseId?: string
  clientId?: string
  onChange: (client: Maybe<ClientOption>) => void
  disabled?: boolean
  autoSelectOnTab?: boolean
}

const DepositClientDropdown: FC<DepositClientDropdownProps> = ({
  caseId,
  clientId,
  onChange,
  disabled,
  autoSelectOnTab = true
}) => {
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([])
  const [selectedClient, setSelectedClient] = useState<Maybe<ClientOption>>(null)

  const [fetchCaseClients, { loading }] = useGetCaseClientsLazyQuery({
    fetchPolicy: GRAPHQL_QUERY_POLICY.fetchPolicy,
    onCompleted(data) {
      if (data?.getCaseClients?.clients) {
        const options = data.getCaseClients.clients
          .filter(role => role?.externalUserToExternalUserRole?.externalUser)
          .map(role => {
            const user = role.externalUserToExternalUserRole!.externalUser!
            const fullName = `${user.displayName?.trim() || ''}`
            const clientNum = user.clientNumber ?? ''
            const roleName = role.caseRole ? `- ${enumToLabel(role.caseRole)}` : ''
            const label = [clientNum ? `(#${clientNum})` : '', fullName, roleName].filter(Boolean).join(' ')

            return {
              id: user.id,
              label,
              caseRole: role.caseRole ?? null,
              clientNumber: clientNum
            }
          })

        setClientOptions(options)

        // Pre-select by clientId from URL, or default to Primary Client
        if (options.length > 0) {
          const byUrl = clientId ? options.find(c => c.id === clientId) : null
          const primary = options.find(c => c.caseRole === ExternalUserCaseRole.PrimaryClient)
          const toSelect = byUrl ?? primary ?? options[0]

          setSelectedClient(toSelect)
          onChange(toSelect)
        }
      }
    },
    onError() {
      setClientOptions([])
    }
  })

  useEffect(() => {
    if (caseId) {
      setSelectedClient(null)
      setClientOptions([])
      fetchCaseClients({
        variables: {
          getCaseClientsInput: {
            caseId
          }
        }
      })
    } else {
      setSelectedClient(null)
      setClientOptions([])
      onChange(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId])

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, value: ClientOption | null) => {
      setSelectedClient(value)
      onChange(value)
    },
    [onChange]
  )

  const getOptionLabel = useCallback((option: ClientOption) => option.label || '', [])

  const isDisabled = disabled || !caseId || loading

  return (
    <Autocomplete
      options={clientOptions}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      onChange={handleChange}
      value={selectedClient}
      autoHighlight={autoSelectOnTab}
      autoSelect={autoSelectOnTab}
      disabled={isDisabled}
      loading={loading}
      ListboxProps={{
        role: 'list-box',
        style: { maxHeight: '190px' }
      }}
      renderInput={params => <TextField {...params} label="Select Client" required />}
      noOptionsText={loading ? 'Loading...' : !caseId ? 'Select a matter first' : 'No clients'}
      disableClearable={false}
    />
  )
}

export default memo(DepositClientDropdown)
